import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../hooks/use-analytics.ts';
import {
    explainableMetrics,
    useMetricCopy,
    type ExplainableMetric,
    type MetricBase,
} from '../hooks/use-metric-copy.ts';

/** The `?` beside a figure's label. Attach it wherever a surface names one of these figures. */
export type MetricInfoHintProps = {
    metric: ExplainableMetric;
    /** What a margin figure was calculated on. */
    base?: MetricBase;
};

export function MetricInfoHint({ metric, base }: MetricInfoHintProps) {
    const { t } = useTranslation('shared');
    const readMetricCopy = useMetricCopy();
    const sendEvent = useAnalytics();
    const { label, explanation } = readMetricCopy(metric, base);

    return (
        <InfoHint
            onOpenChange={(open) => {
                if (open) {
                    sendEvent('metric_hint_opened', { metric });
                }
            }}
        >
            {/* Some of these sit inside a clickable tile, where the tap would otherwise
                reach the tile's link. ponytail: a button inside an anchor is still invalid
                nesting, so lift the title out of the tile if that starts to bite. */}
            <InfoHintTrigger
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
            />
            <InfoHintContent>
                <InfoHintHeader>
                    <InfoHintTitle>{label}</InfoHintTitle>
                </InfoHintHeader>
                <InfoHintBody className="flex flex-col gap-tight">
                    <p>{explanation}</p>
                    <p className="text-content-weaker">{t('metric_explainer__acl_note')}</p>
                </InfoHintBody>
            </InfoHintContent>
        </InfoHint>
    );
}

/**
 * Every figure in one place. Managed by the dialog state manager rather than
 * local state: the settings menu unmounts its pop-over as soon as the dialog
 * takes the click, which would take a locally-owned dialog down with it.
 */
export const MetricExplainerDialog = DialogStateManager.create(() => {
    const { visible, remove } = useDialog();
    const { t } = useTranslation('shared');
    const readMetricCopy = useMetricCopy();

    return (
        <Dialog
            open={visible}
            onOpenChange={(open) => {
                if (!open) {
                    remove();
                }
            }}
        >
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>{t('metric_explainer__title')}</DialogTitle>
                    <DialogIconClose />
                </DialogHeader>
                <DialogBody className="flex flex-col gap-relaxed">
                    <p className="text-body text-content-weaker">{t('metric_explainer__acl_note')}</p>
                    <p className="text-body text-content-weaker">{t('metric_explainer__erp_divergence')}</p>

                    {explainableMetrics.map((metric) => {
                        const { label, explanation } = readMetricCopy(metric);

                        return (
                            <div className="flex flex-col gap-tight" key={metric}>
                                <span className="text-body-emphasis">{label}</span>
                                <p className="text-body text-content-weaker">{explanation}</p>
                            </div>
                        );
                    })}
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
});
